import moment from 'moment';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getDefaultStartEndTime } from '../helpers/getDefaultStartEndTime';
import {
  MetricToggleButtonOptions,
  MonitoringMetricTogglerType,
  rangeType,
  GranularityEnum,
} from '../constants';

import { useFetchMetrics } from '../useFetchMetrics';
import _debounce from 'lodash/debounce';
import { getFilteredMonitoringMetrics } from '../getFilteredMonitoringMetrics';
import { updateLatestTime } from '../helpers/updateLatestTime';
import { useParams } from 'react-router-dom';
import { getAdjustedGranularity } from '../helpers/granularityUtils';
import { AdaptedMetricType } from '../monitoringTypes';

const getInstanceById = (instances: Array<any>, instanceId: string): any =>
  instances?.find((instance) => instance._id === instanceId);

export const useMonitoring = (props: { database }) => {
  const params = useParams();
  const { database } = props;
  const { instances, id: entityId } = database;
  const { tenantDomain, tenantId } = params;

  const primaryNode = instances?.filter(
    (i) => i?.role?.toLowerCase() === 'primary',
  );
  const { id: primaryNodeInstanceId } =
    (primaryNode?.length && primaryNode[0]) || '';
  const rangeMax = moment().subtract(moment().seconds() % 10, 's');

  const [step, setStep] = useState(60);

  const [range, setRange] = useState(Object.keys(rangeType)?.[0]);
  const [instance, setInstance] = useState(primaryNodeInstanceId);
  const [granularity, setGranularity] = useState<GranularityEnum>(
    GranularityEnum['1m'],
  );

  const { initialStartTime, initialEndTime } = getDefaultStartEndTime();
  const initialStartTimestamp = initialStartTime.valueOf();
  const initialEndTimestamp = initialEndTime.valueOf();

  const [startTime, setStartTime] = useState(initialStartTimestamp);
  const [endTime, setEndTime] = useState(initialEndTimestamp);
  const [xMin, setXMin] = useState(initialStartTimestamp);
  const [xMax, setXMax] = useState(initialEndTimestamp);
  const [metricOption, setMetricOption] = useState<MonitoringMetricTogglerType>(
    Object.keys(MetricToggleButtonOptions)[0] as MonitoringMetricTogglerType,
  );
  const [searchText, setSearchText] = useState<Array<string>>([]);
  const [shouldRenderCharts, setShouldRenderCharts] = useState(false);
  const [fullScreenChart, setFullScreenChart] =
    useState<AdaptedMetricType | null>(null);

  const scrollRef = useRef<HTMLDivElement>();

  const {
    response: combinedAdaptedResponse,
    isLoading: combinedIsLoading,
    postData: fetchAllMetrics,
  } = useFetchMetrics({
    serviceInstanceId: instance,
    endTime: endTime,
    startTime: startTime,
    pgSource: tenantDomain,
    tenantId: tenantId,
    tenantDomain,
    entityId: entityId,
    instances: instances,
    instance: instance,
    step: step,
    granularity: granularity,
    status: getInstanceById(instances, instance)?.status,
    updateTimeUsingToggleButton: (resetDisplayDuration: boolean) =>
      updateLatestTime(range, updateStartEndTime, resetDisplayDuration),
  });

  const { availability, dbMetrics } = combinedAdaptedResponse;

  // Auto-adjust granularity when time range changes
  useEffect(() => {
    if (startTime && endTime) {
      setGranularity((currentGranularity) => {
        const adjustedGranularity = getAdjustedGranularity(
          currentGranularity,
          startTime,
          endTime,
        );
        return adjustedGranularity;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  // Fetch metrics when instance, time range, or granularity changes
  // When time range changes, granularity will be adjusted first (above effect),
  // then this effect will run again with the updated granularity value
  useEffect(() => {
    if (startTime && endTime && !combinedIsLoading) {
      // Check if current granularity is valid for the time range
      // If not, skip fetch - the granularity adjustment will trigger it
      const adjustedGranularity = getAdjustedGranularity(
        granularity,
        startTime,
        endTime,
      );

      // Only fetch if granularity is valid (matches adjusted value)
      // This prevents fetching with invalid granularity when time range changes
      if (adjustedGranularity === granularity) {
        fetchAllMetrics();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, startTime, endTime, granularity]);

  const updateStartEndTime = (
    st: number,
    et: number,
    resetDisplayDuration: boolean,
    step: number,
  ) => {
    if (resetDisplayDuration) {
      setXMin(st);
      setXMax(et);
    } else {
      setXMin((prev) => prev + 1);
      setXMax((prev) => prev + 1);
    }
    setStartTime(st);
    setEndTime(et);
    if (step) {
      setStep(step);
    }
  };

  const updateTimeUsingChartZoomIn = (chart: any, options?: any) => {
    const xMin = options?.xaxis?.min;
    const xMax = options?.xaxis?.max;

    setXMin(xMin ?? startTime);
    setXMax(xMax ?? endTime);
    setShouldRenderCharts((prev) => !prev);
  };

  const debounceSearchFn = useCallback(
    _debounce(() => setShouldRenderCharts((prev) => !prev), 500),
    [],
  );

  const updateSearchText = (newSearchValue) => {
    setSearchText(newSearchValue);
    debounceSearchFn();
  };

  const getFilteredCharts = useMemo(() => {
    return getFilteredMonitoringMetrics(dbMetrics, metricOption, searchText);
  }, [dbMetrics, metricOption, searchText]);

  const getLatestXRange = () => ({ xMin, xMax });

  useLayoutEffect(() => {
    setShouldRenderCharts((prev) => !prev);
  }, [metricOption]);

  // Sync fullscreen chart data when metrics update (but only if a chart is open)
  // Use a ref to track the previous dbMetrics length to detect actual updates
  const prevDbMetricsLengthRef = useRef(dbMetrics?.length || 0);
  const fullScreenChartIdRef = useRef<string | null>(null);

  // Track which chart ID should be open
  useEffect(() => {
    if (fullScreenChart?.id) {
      fullScreenChartIdRef.current = fullScreenChart.id;
    } else {
      fullScreenChartIdRef.current = null;
    }
  }, [fullScreenChart?.id]);

  useEffect(() => {
    // Only sync if dbMetrics actually changed (length changed or data refreshed)
    const metricsChanged =
      prevDbMetricsLengthRef.current !== (dbMetrics?.length || 0);
    prevDbMetricsLengthRef.current = dbMetrics?.length || 0;

    if (
      fullScreenChart &&
      fullScreenChartIdRef.current &&
      dbMetrics?.length &&
      metricsChanged
    ) {
      // Find the updated chart data by ID - must match exactly
      const updatedChart = dbMetrics.find(
        (metric) => metric?.id === fullScreenChartIdRef.current,
      );
      if (updatedChart && updatedChart.id === fullScreenChartIdRef.current) {
        // Update with latest chart data
        setFullScreenChart(updatedChart);
      }
    }
  }, [dbMetrics, fullScreenChart, setFullScreenChart]);

  return {
    scrollRef,
    combinedAdaptedResponse,
    startTime,
    endTime,
    searchText,
    updateSearchText,
    metricOption,
    setMetricOption,
    updateStartEndTime,
    fetchAllMetrics,
    range,
    setRange,
    instance,
    setInstance,
    granularity,
    setGranularity,
    xMin,
    xMax,
    updateTimeUsingChartZoomIn,
    availability,
    getLatestXRange,
    dbMetrics,
    getFilteredCharts,
    combinedIsLoading,
    shouldRenderCharts,
    fullScreenChart,
    setFullScreenChart,
  };
};
